import { AlertTriangle, ChevronDown, ChevronRight, Users } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { DuplicatePair } from '@/hooks/useWizardReviewController'

interface DuplicateWarningBannerProps {
  duplicates: DuplicatePair[]
  isExpanded: boolean
  onToggleExpand: () => void
  onMergePair?: (pair: DuplicatePair) => void
}

export function DuplicateWarningBanner({
  duplicates,
  isExpanded,
  onToggleExpand,
  onMergePair
}: DuplicateWarningBannerProps) {
  if (duplicates.length === 0) {
    return null
  }

  return (
    <Alert variant="destructive" className="border-amber-500 bg-amber-50">
      <AlertTriangle className="h-4 w-4 text-amber-600" />
      <AlertTitle className="text-amber-800 flex items-center justify-between">
        <span>
          {duplicates.length} potentiell{duplicates.length === 1 ? '' : 'a'} dubblett{duplicates.length === 1 ? '' : 'er'} hittad{duplicates.length === 1 ? '' : 'es'}
        </span>
        <button
          onClick={onToggleExpand}
          className="flex items-center gap-1 text-sm font-normal text-amber-700 hover:text-amber-900"
        >
          {isExpanded ? (
            <>
              <ChevronDown className="h-4 w-4" />
              Dölj detaljer
            </>
          ) : (
            <>
              <ChevronRight className="h-4 w-4" />
              Visa detaljer
            </>
          )}
        </button>
      </AlertTitle>
      <AlertDescription className="text-amber-700">
        <p className="mt-1">
          Kontrollera att dessa förare inte är samma person innan du låser perioden.
        </p>

        {isExpanded && (
          <div className="mt-4 space-y-3">
            {duplicates.map((pair, index) => (
              <div
                key={`${pair.driver1Id}-${pair.driver2Id}`}
                className="flex items-center justify-between p-3 bg-white rounded-lg border border-amber-200"
              >
                <div className="flex items-center gap-3">
                  <Users className="h-5 w-5 text-amber-600" />
                  <div>
                    <p className="font-medium text-amber-900">
                      {pair.driver1Name} <span className="text-amber-600">&</span> {pair.driver2Name}
                    </p>
                    <p className="text-sm text-amber-700">{pair.details}</p>
                  </div>
                </div>
                {onMergePair && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onMergePair(pair)}
                    className="border-amber-500 text-amber-700 hover:bg-amber-100"
                  >
                    Slå samman
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </AlertDescription>
    </Alert>
  )
}
